import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, useReducedMotion } from 'framer-motion';

import { ArrowBackIcon } from '../../../../assets/svg/SVGComponents';
import { SheetBody, SheetHeader, SheetPanel } from './styles';

interface IEvaluationSheetProps {
  open: boolean;
  title: string;
  subtitle: string;
  onClose: () => void;
  children: React.ReactNode;
}

const SPRING = { type: 'spring', bounce: 0, duration: 0.4 } as const;

/**
 * Folha em tela cheia para avaliar no celular (FE-66). Sobe e desce pelo mesmo
 * caminho; com movimento reduzido, só cross-fade. Sem arraste para fechar: os
 * sliders e a rolagem do formulário não podem disputar o gesto. Fecha pelo
 * botão, pelo Esc e pelo voltar do navegador (a folha entra no histórico).
 */
const EvaluationSheet: React.FC<IEvaluationSheetProps> = ({
  open,
  title,
  subtitle,
  onClose,
  children,
}) => {
  const reduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const pushed = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();

    // Voltar do navegador fecha a folha, em vez de sair do torneio.
    window.history.pushState({ evaluationSheet: true }, '');
    pushed.current = true;
    const onPopState = () => {
      pushed.current = false;
      onCloseRef.current();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('popstate', onPopState);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('keydown', onKeyDown);
      // Fechou pelo botão: tira a entrada que a folha pôs no histórico.
      if (pushed.current) {
        pushed.current = false;
        window.history.back();
      }
    };
  }, [open]);

  const hidden = reduce ? { opacity: 0 } : { y: '100%' };
  const shown = reduce ? { opacity: 1 } : { y: 0 };

  return createPortal(
    <AnimatePresence>
      {open && (
        <SheetPanel
          key="evaluation-sheet"
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={`Avaliar ${title}`}
          tabIndex={-1}
          initial={hidden}
          animate={shown}
          exit={hidden}
          transition={reduce ? { duration: 0.2 } : SPRING}
        >
          <SheetHeader>
            <button
              type="button"
              onClick={onClose}
              aria-label="Voltar à agenda"
            >
              <ArrowBackIcon
                aria-hidden
                width="1.33rem"
                stroke="currentColor"
                strokeWidth={3}
                fill="none"
              />
            </button>
            <div>
              <strong>{title}</strong>
              <small>{subtitle}</small>
            </div>
          </SheetHeader>
          <SheetBody>{children}</SheetBody>
        </SheetPanel>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default EvaluationSheet;
